<template>
	<!--
		Rendered server-side (no <ClientOnly>) so Google indexes the
		Trader's Perspective content. Data is loaded via useAsyncData
		below, which resolves on both the server and the client and is
		hydrated via the SSR payload to avoid a refetch.
	-->
	<div>
		<!-- ── SIDEBAR VARIANT ── -->
		<!--
			Collapsable card with three regions:
			1. Bar  — eye-catching question + sentiment %, click to toggle.
			2. Tease — Trader's Perspective heading + Support/Current/Target
			            range bar, always visible.
			3. Content — intro, bear/bull scenarios, catalysts and deep CTA;
			              collapses to max-h:0 when `isExpanded` is false.
			Defaults to collapsed on first paint (SSR-safe) so the sidebar
			stays compact alongside the article. Tapping the bar or the
			"Read this before you trade" pill expands the content for both
			desktop and mobile viewports.
		-->
		<div
			v-if="perspectiveData && isSidebar"
			class="bg-white rounded-xl border border-slate-100 mb-6 lg:mb-8"
			:data-tpx-expanded="isExpanded"
		>
			<!-- 1. THE BAR -->
			<button
				type="button"
				class="tpx-bar group flex items-center gap-2 w-full px-3 rounded-y-xl py-3 bg-slate-50 border-b border-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#1E3A8A] transition-colors"
				:aria-expanded="isExpanded"
				:aria-controls="tpxContentId"
				@click="toggleExpanded"
			>
				<span
					class="flex-shrink-0 w-7 h-7 rounded-md inline-flex items-center justify-center text-white transition-colors"
					:class="
						isBullish
							? 'bg-[#047857] group-hover:bg-[#065F46]'
							: 'bg-[#B91C1C] group-hover:bg-[#991B1B]'
					"
					aria-hidden="true"
				>
					<svg
						v-if="isBullish"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.25"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-4 h-4"
					>
						<polyline points="3 17 9 11 13 15 21 7" />
						<polyline points="14 7 21 7 21 14" />
					</svg>
					<svg
						v-else
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.25"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-4 h-4"
					>
						<polyline points="3 7 9 13 13 9 21 17" />
						<polyline points="14 17 21 17 21 10" />
					</svg>
				</span>

				<span class="flex-1 text-sm leading-snug text-slate-900 text-left">
					Is it time to buy
					<strong class="font-bold">{{ perspectiveData.ticker }}</strong>
					? It's
					<span
						class="font-semibold"
						:class="isBullish ? 'text-[#047857]' : 'text-[#B91C1C]'"
					>
						{{ formattedPercentOffSupport }}
					</span>
					{{ isBullish ? "off support" : "below support" }}
				</span>

				<span
					class="flex-shrink-0 w-7 h-7 rounded-md bg-[#1E3A8A] group-hover:bg-[#1E40AF] text-white inline-flex items-center justify-center transition-[transform,background-color] duration-300"
					:class="{ 'rotate-90': isExpanded }"
					aria-hidden="true"
				>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.5"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-4 h-4"
					>
						<polyline points="9 18 15 12 9 6" />
					</svg>
				</span>
			</button>

			<!-- 2. TEASE (always visible) -->
			<div class="px-3 pt-3 pb-3">
				<div class="flex items-baseline justify-between gap-2 mb-3">
					<h3
						class="m-0 text-[13px] font-bold tracking-[0.03em] uppercase text-slate-900 title-font"
					>
						Trader's Perspective
					</h3>
					<span class="text-[10px] text-slate-400 tracking-wide">
						Updated {{ updatedShort }}
					</span>
				</div>

				<!-- Range bar -->
				<template v-if="hasRangeData">
					<div
						class="relative h-2 mb-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-full"
					>
						<div
							class="absolute top-1/2 w-[3px] h-3.5 bg-[#2563EB] rounded-sm -translate-x-1/2 -translate-y-1/2"
							:style="{ left: `${currentPercent}%` }"
						/>
					</div>
					<div class="flex justify-between gap-1.5">
						<div class="flex flex-col">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-slate-500"
							>
								Support
							</span>
							<span class="font-mono text-xs font-semibold text-slate-900">
								{{ formatPrice(supportPrice) }}
							</span>
						</div>
						<div class="flex flex-col items-center">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-[#2563EB]"
							>
								Current
							</span>
							<span class="font-mono text-xs font-semibold text-[#2563EB]">
								{{ formatPrice(currentPrice) }}
							</span>
						</div>
						<div class="flex flex-col items-end">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-slate-500"
							>
								Target
							</span>
							<span class="font-mono text-xs font-semibold text-slate-900">
								{{ formatPrice(targetPrice) }}
							</span>
						</div>
					</div>
				</template>

				<!-- Hint pill — only when collapsed -->
				<button
					v-if="!isExpanded"
					type="button"
					class="flex w-full items-center justify-center gap-1.5 mt-3.5 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-900 hover:bg-slate-100 hover:border-slate-300 active:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1E3A8A] transition-colors"
					:aria-controls="tpxContentId"
					:aria-expanded="isExpanded"
					@click="toggleExpanded"
				>
					<span>Read this before you trade</span>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.5"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-3 h-3 text-[#1E3A8A]"
						aria-hidden="true"
					>
						<polyline points="6 9 12 15 18 9" />
					</svg>
				</button>
			</div>

			<!-- 3. EXPANDED CONTENT -->
			<div
				:id="tpxContentId"
				role="region"
				:aria-label="`Bearish and bullish scenarios for ${perspectiveData.ticker}`"
				class="transition-[max-height,border-top-color] duration-300 ease-in-out"
				:class="
					isExpanded
						? 'max-h-[2000px] border-slate-200 opacity-1'
						: 'max-h-0 border-transparent overflow-hidden opacity-0'
				"
			>
				<div class="p-3.5">
					<p
						class="m-0 mb-3.5 text-[12.5px] leading-[1.55] text-slate-500"
						v-html="highlight(perspectiveData.summary)"
					/>

					<div class="flex flex-col gap-2 mb-3.5">
						<div class="rounded-lg py-2.5 px-3 bg-[#FEF2F2] border border-[#FECDD3]">
							<div class="flex items-center gap-1.5 mb-1.5">
								<span
									class="text-[9px] leading-none text-[#B91C1C]"
									aria-hidden="true"
								>
									▼
								</span>
								<span
									class="text-[10.5px] font-bold tracking-[0.06em] uppercase text-[#B91C1C]"
								>
									Bearish Scenario
								</span>
							</div>
							<p
								class="m-0 text-xs leading-[1.5] text-[#7F1D1D]"
								v-html="highlight(perspectiveData.bearish_scenario)"
							/>
						</div>

						<div class="rounded-lg py-2.5 px-3 bg-[#ECFDF5] border border-[#A7F3D0]">
							<div class="flex items-center gap-1.5 mb-1.5">
								<span
									class="text-[9px] leading-none text-[#047857]"
									aria-hidden="true"
								>
									▲
								</span>
								<span
									class="text-[10.5px] font-bold tracking-[0.06em] uppercase text-[#047857]"
								>
									Bullish Scenario
								</span>
							</div>
							<p
								class="m-0 text-xs leading-[1.5] text-[#064E3B]"
								v-html="highlight(perspectiveData.bullish_scenario)"
							/>
						</div>
					</div>

					<div
						v-if="catalysts.length"
						ref="catalystsRoot"
						class="flex flex-col gap-2 py-4 border-t border-slate-100"
					>
						<div class="flex flex-col gap-2">
							<span
								class="text-[11px] font-bold tracking-[0.14em] text-slate-500 shrink-0"
							>
								CATALYSTS:
							</span>
							<div class="flex flex-wrap items-center gap-2">
								<button
									v-for="(cat, i) in catalysts"
									:key="i"
									type="button"
									class="catalyst-chip group/catalyst relative inline-flex items-center gap-2 rounded-lg px-3 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/60 md:cursor-default cursor-pointer"
									:aria-label="cat.tooltip"
									:aria-expanded="activeCatalystIndex === i"
									:aria-controls="`catalyst-detail-${i}`"
									@click="toggleCatalyst(i)"
								>
									<svg
										width="12"
										height="12"
										viewBox="0 0 16 16"
										fill="none"
									>
										<rect
											x="2"
											y="3"
											width="12"
											height="11"
											rx="1.5"
											stroke="#ffffff"
											stroke-width="1.3"
										/>
										<path
											d="M2 6H14"
											stroke="#ffffff"
											stroke-width="1.3"
										/>
										<path
											d="M6 1V4M10 1V4"
											stroke="#ffffff"
											stroke-width="1.3"
											stroke-linecap="round"
										/>
									</svg>
									<span class="text-xs font-medium text-white">
										{{ cat.label }}
										<template v-if="cat.dateText">: {{ cat.dateText }}</template>
									</span>

									<!-- Desktop tooltip (≥ md only) — hover/focus controlled. -->
									<div
										role="tooltip"
										class="catalyst-tooltip pointer-events-none absolute left-1/2 bottom-full mb-2 -translate-x-1/2 translate-y-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12px] font-medium leading-snug opacity-0 shadow-lg shadow-slate-900/20 transition duration-200 ease-out z-10 hidden md:block group-hover/catalyst:opacity-100 group-hover/catalyst:translate-y-0 group-focus-visible/catalyst:opacity-100 group-focus-visible/catalyst:translate-y-0"
									>
										{{ cat.tooltip }}
										<span
											class="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[4px] border-x-transparent border-t-[4px] border-t-slate-900"
										/>
									</div>
								</button>
							</div>
						</div>

						<!--
					Mobile detail panel (< md only). Rendered as a sibling of the
					chip strip so it flows in normal layout — no absolute
					positioning, no viewport-edge overflow. The chip's
					`aria-controls` references the panel's id for assistive tech.
				-->
						<div
							v-if="activeCatalyst"
							:id="`catalyst-detail-${activeCatalystIndex}`"
							role="region"
							:aria-label="`Details for ${activeCatalyst.label}`"
							class="md:hidden mt-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12px] font-medium leading-snug shadow-md"
						>
							<span class="block text-[10px] uppercase tracking-wider text-slate-400">
								{{ activeCatalyst.label }}
								<template v-if="activeCatalyst.dateText">
									: {{ activeCatalyst.dateText }}
								</template>
							</span>
							<span class="block mt-1">
								{{ activeCatalyst.description }}
							</span>
						</div>
					</div>

					<a
						:href="quoteHref"
						class="flex items-center justify-center gap-2 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-[13px] font-semibold tracking-[0.02em] px-3.5 py-3 rounded-lg transition-colors no-underline active:translate-y-px"
					>
						Get the Full Trade Plan
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2.5"
							stroke-linecap="round"
							stroke-linejoin="round"
							class="w-3.5 h-3.5"
							aria-hidden="true"
						>
							<line
								x1="5"
								y1="12"
								x2="19"
								y2="12"
							/>
							<polyline points="12 5 19 12 12 19" />
						</svg>
					</a>
				</div>
			</div>
		</div>

		<!-- ── EMBEDDED VARIANT ──
			Renders the same data as the sidebar's expanded body, but always
			visible. The parent owns the show/hide decision (e.g. the news
			article's mobile "Is it time to buy …?" tap-to-reveal bar), so
			there is no internal toggle UI here. CTA href can be overridden
			via `ctaHref` so the parent can route to its own destination.
		-->
		<div
			v-else-if="perspectiveData && embedded"
			class="bg-white rounded-xl"
		>
			<div class="px-4 pt-4 pb-3">
				<div class="flex items-baseline justify-between gap-2 mb-3">
					<h3
						class="m-0 text-[13px] font-bold tracking-[0.03em] uppercase text-slate-900 title-font"
					>
						Trader's Perspective
					</h3>
					<span class="text-[10px] text-slate-400 tracking-wide">
						Updated {{ updatedShort }}
					</span>
				</div>

				<template v-if="hasRangeData">
					<div
						class="relative h-2 mb-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-full"
					>
						<div
							class="absolute top-1/2 w-[3px] h-3.5 bg-[#2563EB] rounded-sm -translate-x-1/2 -translate-y-1/2"
							:style="{ left: `${currentPercent}%` }"
						/>
					</div>
					<div class="flex justify-between gap-1.5">
						<div class="flex flex-col">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-slate-500"
							>
								Support
							</span>
							<span class="font-mono text-xs font-semibold text-slate-900">
								{{ formatPrice(supportPrice) }}
							</span>
						</div>
						<div class="flex flex-col items-center">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-[#2563EB]"
							>
								Current
							</span>
							<span class="font-mono text-xs font-semibold text-[#2563EB]">
								{{ formatPrice(currentPrice) }}
							</span>
						</div>
						<div class="flex flex-col items-end">
							<span
								class="text-[9.5px] font-bold tracking-[0.06em] uppercase text-slate-500"
							>
								Target
							</span>
							<span class="font-mono text-xs font-semibold text-slate-900">
								{{ formatPrice(targetPrice) }}
							</span>
						</div>
					</div>
				</template>
			</div>

			<div class="px-4 pb-4">
				<p
					class="m-0 mb-3.5 text-[12.5px] leading-[1.55] text-slate-500"
					v-html="highlight(perspectiveData.summary)"
				/>

				<div class="flex flex-col gap-2 mb-3.5">
					<div class="rounded-lg py-2.5 px-3 bg-[#FEF2F2] border border-[#FECDD3]">
						<div class="flex items-center gap-1.5 mb-1.5">
							<span
								class="text-[9px] leading-none text-[#B91C1C]"
								aria-hidden="true"
							>
								▼
							</span>
							<span
								class="text-[10.5px] font-bold tracking-[0.06em] uppercase text-[#B91C1C]"
							>
								Bearish Scenario
							</span>
						</div>
						<p
							class="m-0 text-xs leading-[1.5] text-[#7F1D1D]"
							v-html="highlight(perspectiveData.bearish_scenario)"
						/>
					</div>

					<div class="rounded-lg py-2.5 px-3 bg-[#ECFDF5] border border-[#A7F3D0]">
						<div class="flex items-center gap-1.5 mb-1.5">
							<span
								class="text-[9px] leading-none text-[#047857]"
								aria-hidden="true"
							>
								▲
							</span>
							<span
								class="text-[10.5px] font-bold tracking-[0.06em] uppercase text-[#047857]"
							>
								Bullish Scenario
							</span>
						</div>
						<p
							class="m-0 text-xs leading-[1.5] text-[#064E3B]"
							v-html="highlight(perspectiveData.bullish_scenario)"
						/>
					</div>
				</div>

				<div
					v-if="catalysts.length"
					ref="catalystsRoot"
					class="flex flex-col gap-2 py-3 border-t border-slate-100 mb-3"
				>
					<span
						class="text-[11px] font-bold tracking-[0.14em] text-slate-500 shrink-0"
					>
						CATALYSTS:
					</span>
					<div class="flex flex-wrap items-center gap-2">
						<button
							v-for="(cat, i) in catalysts"
							:key="i"
							type="button"
							class="catalyst-chip group/catalyst relative inline-flex items-center gap-2 rounded-lg px-3 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/60 cursor-pointer"
							:aria-label="cat.tooltip"
							:aria-expanded="activeCatalystIndex === i"
							:aria-controls="`catalyst-detail-${i}`"
							@click="toggleCatalyst(i)"
						>
							<svg
								width="12"
								height="12"
								viewBox="0 0 16 16"
								fill="none"
							>
								<rect
									x="2"
									y="3"
									width="12"
									height="11"
									rx="1.5"
									stroke="#ffffff"
									stroke-width="1.3"
								/>
								<path
									d="M2 6H14"
									stroke="#ffffff"
									stroke-width="1.3"
								/>
								<path
									d="M6 1V4M10 1V4"
									stroke="#ffffff"
									stroke-width="1.3"
									stroke-linecap="round"
								/>
							</svg>
							<span class="text-xs font-medium text-white">
								{{ cat.label }}
								<template v-if="cat.dateText">: {{ cat.dateText }}</template>
							</span>
						</button>
					</div>

					<div
						v-if="activeCatalyst"
						:id="`catalyst-detail-${activeCatalystIndex}`"
						role="region"
						:aria-label="`Details for ${activeCatalyst.label}`"
						class="mt-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12px] font-medium leading-snug shadow-md"
					>
						<span class="block text-[10px] uppercase tracking-wider text-slate-400">
							{{ activeCatalyst.label }}
							<template v-if="activeCatalyst.dateText">
								: {{ activeCatalyst.dateText }}
							</template>
						</span>
						<span class="block mt-1">
							{{ activeCatalyst.description }}
						</span>
					</div>
				</div>

				<a
					:href="quoteHref"
					:target="ctaHref ? '_blank' : undefined"
					:rel="ctaHref ? 'noopener noreferrer' : undefined"
					class="flex items-center justify-center gap-2 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-[13px] font-semibold tracking-[0.02em] px-3.5 py-3 rounded-lg transition-colors no-underline active:translate-y-px"
				>
					Get the Full Trade Plan
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.5"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-3.5 h-3.5"
						aria-hidden="true"
					>
						<line
							x1="5"
							y1="12"
							x2="19"
							y2="12"
						/>
						<polyline points="12 5 19 12 12 19" />
					</svg>
				</a>
			</div>
		</div>

		<!-- ── DEFAULT VARIANT ── -->
		<div
			v-else-if="perspectiveData"
			class="bg-white rounded-2xl border border-slate-100 lg:border-none flex flex-col"
		>
			<!-- Header + summary -->
			<div class="flex flex-col gap-4 p-5 lg:p-6">
				<h2 class="text-xl lg:text-2xl font-bold text-slate-900 title-font">
					Trader's Perspective
				</h2>
				<p
					class="text-sm text-slate-600 leading-relaxed"
					v-html="highlight(perspectiveData.summary)"
				/>
			</div>

			<!-- Range bar -->
			<div
				v-if="hasRangeData"
				class="flex flex-col lg:gap-2 px-5 lg:px-6 py-4 bg-slate-100/[0.25] border-x-2 border-white"
			>
				<div class="flex items-center gap-3">
					<span
						class="text-[11px] font-bold tracking-[0.14em] text-slate-500 shrink-0"
					>
						RANGE
					</span>
					<div class="flex-1 relative h-1 bg-slate-200 rounded-full">
						<div
							class="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-slate-500 rounded"
						/>
						<div
							class="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-slate-500 rounded"
						/>
						<div
							class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1 h-4 bg-blue-500 rounded-sm shadow-sm"
							:style="{ left: `${currentPercent}%` }"
						/>
					</div>
				</div>
				<!-- Desktop labels: CURRENT gets its own row so it never overlaps Support/Target -->
				<div class="hidden md:flex md:flex-col md:gap-1 ml-[52px]">
					<div class="relative h-4">
						<span
							class="absolute top-0 text-[11px] font-bold text-blue-500 whitespace-nowrap"
							:style="currentLabelStyle"
						>
							CURRENT: {{ formatPrice(currentPrice) }}
						</span>
					</div>
					<div class="flex items-center justify-between">
						<span class="text-[11px] font-semibold text-slate-600 whitespace-nowrap">
							Support: {{ formatPrice(supportPrice) }}
						</span>
						<span class="text-[11px] font-semibold text-slate-600 whitespace-nowrap">
							Target: {{ formatPrice(targetPrice) }}
						</span>
					</div>
				</div>
				<!-- Mobile labels: single row (Support · Current · Target) — current stays blue to match the indicator -->
				<div class="md:hidden flex items-center justify-between">
					<span class="text-[11px] font-semibold text-slate-600 whitespace-nowrap">
						Support: {{ formatPrice(supportPrice) }}
					</span>
					<span
						class="text-[11px] font-bold text-blue-500 whitespace-nowrap min-w-[92px]"
					>
						<span class="text-slate-600">Current:</span>
						{{ formatPrice(currentPrice) }}
					</span>
					<span class="text-[11px] font-semibold text-slate-600 whitespace-nowrap">
						Target: {{ formatPrice(targetPrice) }}
					</span>
				</div>
			</div>

			<!-- Scenarios -->
			<div class="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 lg:px-6 py-4">
				<!-- Bearish -->
				<div
					class="order-2 md:order-1 rounded-xl p-3 lg:p-4 bg-[#FEF2F2] border border-[#FECDD3] flex flex-col gap-1 lg:gap-2"
				>
					<div class="flex items-center gap-1.5">
						<svg
							width="10"
							height="10"
							viewBox="0 0 10 10"
							fill="none"
						>
							<path
								d="M1 3L5 8L9 3H1Z"
								fill="#B91C1C"
							/>
						</svg>
						<span class="text-[11px] font-bold text-[#B91C1C] tracking-wide">
							BEARISH SCENARIO
						</span>
					</div>
					<p
						class="text-[13px] text-[#7F1D1D] leading-relaxed"
						v-html="highlight(perspectiveData.bearish_scenario)"
					/>
				</div>

				<!-- Bullish -->
				<div
					class="order-1 md:order-2 rounded-xl p-3 lg:p-4 bg-[#ECFDF5] border border-[#A7F3D0] flex flex-col gap-1 lg:gap-2"
				>
					<div class="flex items-center gap-1.5">
						<svg
							width="10"
							height="10"
							viewBox="0 0 10 10"
							fill="none"
						>
							<path
								d="M5 2L9 7H1L5 2Z"
								fill="#047857"
							/>
						</svg>
						<span class="text-[11px] font-bold text-[#047857] tracking-wide">
							BULLISH SCENARIO
						</span>
					</div>
					<p
						class="text-[13px] text-[#064E3B] leading-relaxed"
						v-html="highlight(perspectiveData.bullish_scenario)"
					/>
				</div>
			</div>

			<!-- Catalysts -->
			<!--
				Two interaction modes share the same chip data:
				• ≥ md  — desktop hover/focus reveals a floating tooltip above the chip.
				• < md  — mobile tap toggles a full-width panel rendered BELOW the
				          chip strip. The floating tooltip is hidden so it can't
				          overflow the viewport edge on narrow screens (the bug
				          this section originally had).
			-->
			<div
				v-if="catalysts.length"
				ref="catalystsRoot"
				class="flex flex-col gap-2 px-5 lg:px-6 py-3 border-t border-slate-100"
			>
				<div class="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2">
					<span
						class="text-[11px] font-bold tracking-[0.14em] text-slate-500 shrink-0"
					>
						CATALYSTS:
					</span>
					<div class="flex flex-wrap items-center gap-2">
						<button
							v-for="(cat, i) in catalysts"
							:key="i"
							type="button"
							class="catalyst-chip group/catalyst relative inline-flex items-center gap-2 rounded-lg px-3 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/60 md:cursor-default cursor-pointer"
							:aria-label="cat.tooltip"
							:aria-expanded="activeCatalystIndex === i"
							:aria-controls="`catalyst-detail-${i}`"
							@click="toggleCatalyst(i)"
						>
							<svg
								width="12"
								height="12"
								viewBox="0 0 16 16"
								fill="none"
							>
								<rect
									x="2"
									y="3"
									width="12"
									height="11"
									rx="1.5"
									stroke="#ffffff"
									stroke-width="1.3"
								/>
								<path
									d="M2 6H14"
									stroke="#ffffff"
									stroke-width="1.3"
								/>
								<path
									d="M6 1V4M10 1V4"
									stroke="#ffffff"
									stroke-width="1.3"
									stroke-linecap="round"
								/>
							</svg>
							<span class="text-xs font-medium text-white">
								{{ cat.label }}
								<template v-if="cat.dateText">: {{ cat.dateText }}</template>
							</span>

							<!-- Desktop tooltip (≥ md only) — hover/focus controlled. -->
							<div
								role="tooltip"
								class="catalyst-tooltip pointer-events-none absolute left-1/2 bottom-full mb-2 -translate-x-1/2 translate-y-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12px] font-medium leading-snug opacity-0 shadow-lg shadow-slate-900/20 transition duration-200 ease-out z-10 hidden md:block group-hover/catalyst:opacity-100 group-hover/catalyst:translate-y-0 group-focus-visible/catalyst:opacity-100 group-focus-visible/catalyst:translate-y-0"
							>
								{{ cat.tooltip }}
								<span
									class="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[4px] border-x-transparent border-t-[4px] border-t-slate-900"
								/>
							</div>
						</button>
					</div>
				</div>

				<!--
					Mobile detail panel (< md only). Rendered as a sibling of the
					chip strip so it flows in normal layout — no absolute
					positioning, no viewport-edge overflow. The chip's
					`aria-controls` references the panel's id for assistive tech.
				-->
				<div
					v-if="activeCatalyst"
					:id="`catalyst-detail-${activeCatalystIndex}`"
					role="region"
					:aria-label="`Details for ${activeCatalyst.label}`"
					class="md:hidden mt-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12px] font-medium leading-snug shadow-md"
				>
					<span class="block text-[10px] uppercase tracking-wider text-slate-400">
						{{ activeCatalyst.label }}
						<template v-if="activeCatalyst.dateText">
							: {{ activeCatalyst.dateText }}
						</template>
					</span>
					<span class="block mt-1">
						{{ activeCatalyst.description }}
					</span>
				</div>
			</div>

			<!-- Footer -->
			<div
				class="flex justify-center lg:justify-end pt-2 border-t border-slate-100 p-5 lg:p-6"
			>
				<span class="text-xs text-slate-400">
					Analysis generated: {{ formattedGeneratedAt }}
				</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Mirrors the `UpcomingCatalyst` shape emitted by the songbird API
 * (`src/utils/trading-perspective-generator.ts`). The API guarantees this
 * field is always populated — the route's `buildResponse` will derive it
 * from `watch_for` server-side when an older cached record was written
 * before the structured-catalyst schema. The widget can therefore treat
 * it as the single source of truth for the chip strip.
 *
 * `date` is optional: dated catalysts (Earnings, Approval, etc.) carry a
 * specific calendar date, while undated themes ("Hot Sector", "Geopolitical",
 * "AI") render as label-only chips for ongoing macro/sector situations.
 */
interface UpcomingCatalyst {
	/** ISO YYYY-MM-DD; absent for undated/ongoing themes. */
	date?: string;
	/** 1–2 word title used as the chip label */
	label: string;
	/** 1–2 sentence tooltip body, ticker-specific */
	description: string;
}

interface TradingPerspectiveData {
	ticker: string;
	summary: string;
	bullish_scenario: string;
	bearish_scenario: string;
	watch_for: string;
	/**
	 * Server-resolved catalyst list — always present in API responses.
	 * Optional in this interface only as a defensive type guard for the
	 * (now-impossible) case of a malformed response; the runtime code
	 * just renders nothing rather than attempting any client-side
	 * derivation.
	 */
	upcoming_catalysts?: UpcomingCatalyst[];
	current_price: string;
	price_at_generation: number;
	market_cap: string;
	primary_catalyst: string;
	bullish_target: string;
	bearish_level: string;
	refresh_tier: string;
	generated_date: string;
	generated_at: string;
	ttl_days: number;
	cache_status: string;
	cache_age_hours: number;
}

interface Catalyst {
	label: string;
	/** Composite "Label: Description" string used as the desktop tooltip body. */
	tooltip: string;
	/** Raw description, kept separate so the mobile panel can render it
	 *  alongside its own header (label + date) for visual hierarchy. */
	description: string;
	/** Display string like "May 20"; absent for undated themes. */
	dateText?: string;
	/** Days until the event from today; absent for undated themes. */
	days?: number;
}

const props = defineProps<{
	symbol: string;
	isSidebar?: boolean;
	/**
	 * Content-only variant: render the perspective data inline without the
	 * collapsable bar/teaser UI. Visibility is owned by the parent (e.g. the
	 * news article's mobile "Is it time to buy …?" tap-to-reveal). Takes
	 * precedence after `isSidebar` so `isSidebar` is still respected when
	 * both flags are set on the same instance.
	 */
	embedded?: boolean;
	/**
	 * Optional override for the "Get the Full Trade Plan" link. When set,
	 * the CTA opens this URL in a new tab (treated as an external link).
	 * Defaults to the symbol's `/quote/` page when omitted.
	 */
	ctaHref?: string;
}>();

const { songbirdProxy } = useRuntimeConfig().public;

/**
 * Fetch the Trader's Perspective payload on both the server and the
 * client via useAsyncData. Server-rendering this widget matters for SEO
 * (Google indexes the summary/scenarios/catalysts text) and for hard
 * page loads on Cloudflare Pages, where the previous onMounted-only
 * approach left the widget missing on initial render and only made it
 * appear after a client-side mount cycle. The result is hydrated from
 * the SSR payload, so the client does not refetch on first paint.
 *
 * The key is symbol-scoped so two pages mounted with different symbols
 * do not share cache entries. `watch` ensures the data refreshes if the
 * parent ever swaps the symbol prop in place (e.g. news sidebar widget
 * when the primary referenced ticker changes).
 */
const { data: perspectiveData } =
	await useAsyncData<TradingPerspectiveData | null>(
		`trading-perspective-${props.symbol || "none"}`,
		async () => {
			if (!props.symbol) return null;
			try {
				const response = await $fetch<any>(
					`${songbirdProxy}stocks/trading-perspective`,
					{
						params: { symbol: props.symbol },
						// 8s upper bound: SSR budget on Cloudflare Pages is tight,
						// so we cap below the upstream's 15s tolerance to avoid
						// stalling the HTML response if Songbird is slow.
						timeout: 8000
					}
				);
				if (!response?.ticker) return null;
				const data = { ...response } as TradingPerspectiveData & {
					perspective?: string;
				};
				if (!data.summary && data.perspective) {
					data.summary = data.perspective;
				}
				if (!data.summary) return null;
				return data;
			} catch {
				// Silently ignore — widget is hidden when there's no data.
				// Errors here include non-2xx responses, timeouts, and
				// Songbird transient failures (fallback layer handles retry).
				return null;
			}
		},
		{ watch: [() => props.symbol] }
	);

// ── Mobile catalyst panel state ────────────────────────────────────────────────
// Index of the chip whose detail panel is currently open on mobile, or null
// when no panel is showing. Desktop hover/focus uses CSS only and ignores
// this state entirely.
const activeCatalystIndex = ref<number | null>(null);
const catalystsRoot = ref<HTMLElement | null>(null);

// ── Sidebar collapsable state ──────────────────────────────────────────────────
// The sidebar variant ships as a "tease + tap to reveal" card so the article
// rail stays compact on first paint. Starts collapsed on both server and
// client (SSR-safe — no hydration mismatch) and toggles on bar/hint click.
const isExpanded = ref(false);

function toggleExpanded(): void {
	isExpanded.value = !isExpanded.value;
}

// Stable id so the bar/hint buttons can reference the collapsable region
// via aria-controls without colliding when multiple widgets co-exist.
const tpxContentId = computed(
	() => `tpx-content-${props.symbol?.toLowerCase() || "x"}`
);

function parsePrice(raw: unknown): number | null {
	if (raw == null) return null;
	if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
	const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ""));
	return Number.isFinite(n) ? n : null;
}

function formatPrice(n: number | null): string {
	if (n == null) return "";
	const fixed = Number.isInteger(n) ? n.toString() : n.toFixed(2);
	return `$${fixed}`;
}

// Inline-formatter that bolds dollar amounts and month-day-(year) date phrases.
// Strips any markdown-style citation links that sometimes appear in the text.
function highlight(text: string | undefined | null): string {
	if (!text) return "";
	const escapeHtml = (s: string) =>
		s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	let out = escapeHtml(text);
	// Remove markdown links like ([host](url)) or [text](url)
	out = out.replace(/\s*\(?\[[^\]]*\]\([^)]*\)\)?/g, "");
	// Bold dollar amounts
	out = out.replace(
		/\$\d[\d,]*(?:\.\d+)?/g,
		(m) => `<strong class="font-semibold">${m}</strong>`
	);
	// Bold month-day(-year) dates
	out = out.replace(
		/\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}(?:,\s?\d{4})?/g,
		(m) => `<strong class="font-semibold">${m}</strong>`
	);
	return out;
}

const supportPrice = computed(() =>
	parsePrice(perspectiveData.value?.bearish_level)
);
const targetPrice = computed(() =>
	parsePrice(perspectiveData.value?.bullish_target)
);
const currentPrice = computed(() => {
	const num = perspectiveData.value?.price_at_generation;
	if (typeof num === "number" && Number.isFinite(num)) return num;
	return parsePrice(perspectiveData.value?.current_price);
});

const hasRangeData = computed(
	() =>
		supportPrice.value != null &&
		targetPrice.value != null &&
		currentPrice.value != null &&
		targetPrice.value > supportPrice.value
);

const currentPercent = computed(() => {
	if (!hasRangeData.value) return 50;
	const pct =
		((currentPrice.value! - supportPrice.value!) /
			(targetPrice.value! - supportPrice.value!)) *
		100;
	return Math.max(0, Math.min(100, pct));
});

// ── Sidebar bar/teaser derivations ─────────────────────────────────────────────
// Sentiment for the colored trend icon and the percentage text in the bar.
// Bullish when the live (or generation-time) price is at or above the
// support level — i.e. the stock has lifted "off support". When it has
// broken below support we flip to a red bearish framing so the question
// stays editorially honest instead of always promising upside.
const isBullish = computed(() => {
	if (currentPrice.value == null || supportPrice.value == null) return true;
	return currentPrice.value >= supportPrice.value;
});

// Distance from support as a percentage. Computed against `supportPrice`
// (not the support→target range used by the slider) so the bar reads like
// a trader-speak quote: "It's up +5.65% off support" / "down 2.10% below
// support". Falls back to "" when prices aren't available so the bar
// gracefully omits the figure rather than rendering NaN%.
const formattedPercentOffSupport = computed(() => {
	if (
		currentPrice.value == null ||
		supportPrice.value == null ||
		supportPrice.value === 0
	)
		return "";
	const pct =
		((currentPrice.value - supportPrice.value) / supportPrice.value) * 100;
	const sign = pct >= 0 ? "+" : "";
	return `${sign}${pct.toFixed(2)}%`;
});

// "Apr 15" style short date for the tease meta line. Uses the same ET
// timezone treatment as `formattedGeneratedAt` so the two never disagree
// when both happen to render together.
const updatedShort = computed(() => {
	if (!perspectiveData.value?.generated_at) return "";
	try {
		const d = dayjs
			.utc(perspectiveData.value.generated_at)
			.tz("America/New_York");
		if (!d.isValid()) return perspectiveData.value.generated_date;
		return d.format("MMM D");
	} catch {
		return perspectiveData.value.generated_date;
	}
});

// "Get the Full Trade Plan" CTA — defaults to the symbol's quote page,
// which is where the full Trader's Perspective default variant lives.
// When `ctaHref` is passed (e.g. the news mobile widget routes traders to
// the watchlist signup), use that destination instead.
const quoteHref = computed(() => {
	if (props.ctaHref) return props.ctaHref;
	const symbol = perspectiveData.value?.ticker || props.symbol;
	return symbol ? `/quote/${symbol}` : "#";
});

// Anchor the CURRENT label so it can't escape the bar's bounds at the extremes.
const currentLabelStyle = computed(() => {
	const pct = currentPercent.value;
	if (pct <= 8) return { left: "0%", transform: "translateX(0)" };
	if (pct >= 92) return { left: "100%", transform: "translateX(-100%)" };
	return { left: `${pct}%`, transform: "translateX(-50%)" };
});

/**
 * Map the API's `upcoming_catalysts` array onto the chip-strip view-model.
 * Source-of-truth for label/description is now the backend (see the
 * `resolveUpcomingCatalysts` helper in songbird's
 * `trading-perspective-generator.ts`), so the widget no longer extracts
 * dates from `watch_for` or applies any heuristic labelling — that lives
 * server-side where it can be vetted, sanitized, and persisted.
 *
 * Two entry shapes are handled uniformly:
 *   • Dated   — `date` is a YYYY-MM-DD string. Chip renders as `Label: May 20`.
 *   • Undated — `date` is missing (sector / macro / geopolitical themes).
 *               Chip renders as just `Label`. No past-date filter applies.
 *
 * What this computed still owns:
 *   - Filtering out entries that are malformed or whose date has passed
 *     (defence in depth; the API does the same check at write time).
 *   - Formatting the ISO date into the chip's "Month D" display string
 *     when present.
 *   - Composing the tooltip prefix ("Label: Description") so the dark
 *     bubble reads like a real annotation rather than a bare sentence.
 *   - Capping at 3 chips to match the chip-strip layout budget.
 */
const catalysts = computed<Catalyst[]>(() => {
	const list = perspectiveData.value?.upcoming_catalysts;
	if (!Array.isArray(list) || list.length === 0) return [];

	const now = dayjs();
	const out: Catalyst[] = [];

	for (const entry of list) {
		if (!entry?.label || !entry?.description) continue;

		const view: Catalyst = {
			label: entry.label,
			tooltip: `${entry.label}: ${entry.description}`,
			description: entry.description
		};

		if (entry.date) {
			// `dayjs(isoString)` natively parses YYYY-MM-DD without needing
			// the customParseFormat plugin (which isn't loaded here).
			const parsed = dayjs(entry.date);
			if (!parsed.isValid()) continue;
			const days = parsed.startOf("day").diff(now.startOf("day"), "day");
			if (days < 0) continue;
			view.dateText = parsed.format("MMMM D");
			view.days = days;
		}

		out.push(view);
	}

	return out.slice(0, 3);
});

// ── Mobile panel helpers ───────────────────────────────────────────────────────

const activeCatalyst = computed<Catalyst | null>(() => {
	const idx = activeCatalystIndex.value;
	if (idx === null) return null;
	return catalysts.value[idx] ?? null;
});

/**
 * Mobile tap handler: tapping the same chip again closes the panel,
 * tapping a different chip switches focus to it. No-op on desktop where
 * the hover/focus tooltip handles disclosure via CSS.
 */
function toggleCatalyst(i: number): void {
	activeCatalystIndex.value = activeCatalystIndex.value === i ? null : i;
}

/**
 * Close the mobile panel when the user taps anywhere outside the catalysts
 * region. Listening on `pointerdown` rather than `click` so the dismiss
 * fires before any incoming tap target's own handlers (avoids the panel
 * briefly flashing closed→open when the user taps a different chip).
 */
function handleOutsidePointer(event: PointerEvent): void {
	if (activeCatalystIndex.value === null) return;
	const root = catalystsRoot.value;
	if (!root) return;
	if (event.target instanceof Node && root.contains(event.target)) return;
	activeCatalystIndex.value = null;
}

/**
 * Auto-close the panel if the underlying catalyst list changes (new symbol,
 * cache refresh, etc.) so we never end up showing stale content for an index
 * that no longer exists.
 */
watch(
	() => catalysts.value.length,
	() => {
		activeCatalystIndex.value = null;
	}
);

onMounted(() => {
	if (typeof window !== "undefined") {
		window.addEventListener("pointerdown", handleOutsidePointer);
	}
});

onBeforeUnmount(() => {
	if (typeof window !== "undefined") {
		window.removeEventListener("pointerdown", handleOutsidePointer);
	}
});

const formattedGeneratedAt = computed(() => {
	if (!perspectiveData.value?.generated_at) return "";
	try {
		const d = dayjs
			.utc(perspectiveData.value.generated_at)
			.tz("America/New_York");
		if (!d.isValid()) return perspectiveData.value.generated_date;
		return d.format("MMMM D, YYYY [at] h:mm A [ET]");
	} catch {
		return perspectiveData.value.generated_date;
	}
});
</script>

<style scoped>
.catalyst-tooltip {
	/* Cap width so 1-2 sentence descriptions wrap into a readable block
	   instead of stretching the chip strip across the whole card. */
	width: max-content;
	max-width: min(280px, 80vw);
	white-space: normal;
	word-break: normal;
	overflow-wrap: anywhere;
	text-align: left;
}

.catalyst-chip {
	/* Reset native <button> defaults so the gradient is the only visible
	   background and the chip inherits its text styling from the parent. */
	background-color: transparent;
	font: inherit;
	color: inherit;
	-webkit-appearance: none;
	appearance: none;

	border: 1px solid rgba(99, 102, 241, 0.45);
	background-image: linear-gradient(
		90deg,
		#3b82f6 0%,
		#6366f1 25%,
		#8b5cf6 50%,
		#6366f1 75%,
		#3b82f6 100%
	);
	background-size: 200% 100%;
	background-position: 0% 50%;
	animation: catalyst-gradient-shift 7s linear infinite;
	transition: border-color 0.2s ease;
}

.catalyst-chip:hover,
.catalyst-chip:focus-visible {
	border-color: rgba(99, 102, 241, 0.7);
}

@keyframes catalyst-gradient-shift {
	0% {
		background-position: 0% 50%;
	}
	100% {
		background-position: 200% 50%;
	}
}

@media (prefers-reduced-motion: reduce) {
	.catalyst-chip {
		animation: none;
	}
}
</style>
